import { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { isAxiosError } from 'axios';
import {
  getLiveSession,
  getLiveSnapshot,
  liveSubscriptionUrl
} from './live-transcript.ts';
import {
  LiveSessionStatus,
  LiveSnapshot
} from '@utils/types/editors/live-transcript.ts';

type ErrorState = 'load' | 'save' | 'conflict' | 'connection' | null;
type Options = {
  sessionId: string;
  cursor: MutableRefObject<number>;
  onSnapshot: (page: LiveSnapshot) => void;
  setDocumentId: Dispatch<SetStateAction<string | null>>;
  setCanEdit: Dispatch<SetStateAction<boolean>>;
  setPartial: Dispatch<SetStateAction<string>>;
  setConnected: Dispatch<SetStateAction<boolean>>;
  setLoading: Dispatch<SetStateAction<boolean>>;
  setError: Dispatch<SetStateAction<ErrorState>>;
  setOwner: Dispatch<SetStateAction<LiveSessionStatus | null>>;
};

export const connectLiveTranscript = ({
  sessionId,
  cursor,
  onSnapshot,
  setDocumentId,
  setCanEdit,
  setPartial,
  setConnected,
  setLoading,
  setError,
  setOwner
}: Options) => {
  const abort = new AbortController();
  let active = true;
  let socket: WebSocket | null = null;
  let reconnect: ReturnType<typeof setTimeout> | undefined;
  let retry: ReturnType<typeof setTimeout> | undefined;
  let busy = false;
  let requested = false;
  let terminal = false;
  let endedFromApi = false;
  let desiredCursor = 0;
  const fetchTail = async () => {
    if (!active) return;
    if (busy) {
      requested = true;
      return;
    }
    busy = true;
    try {
      do {
        requested = false;
        let more: boolean;
        do {
          const page = await getLiveSnapshot(
            sessionId,
            cursor.current,
            abort.signal
          );
          if (!active) return;
          onSnapshot(page);
          setDocumentId(page.document_id);
          setCanEdit(page.can_edit);
          if (page.ended) {
            endedFromApi = terminal = true;
            clearTimeout(reconnect);
            setPartial('');
            setOwner(previous => ({
              request_id: '',
              session_id: sessionId,
              state: previous?.state === 'failed' ? 'failed' : 'stopped',
              error: previous?.state === 'failed' ? previous.error : null,
              last_sequence: page.last_sequence,
              can_stop: false
            }));
            setError(previous => (previous === 'connection' ? null : previous));
            if (socket) {
              socket.onclose = null;
              socket.close();
              socket = null;
              setConnected(false);
            }
          }
          cursor.current = page.next_cursor;
          more = page.has_more;
        } while (more);
        setLoading(false);
        setError(previous => (previous === 'load' ? null : previous));
      } while (requested);
      if (cursor.current < desiredCursor && active) {
        retry = setTimeout(() => {
          void fetchTail();
        }, 500);
      }
    } catch (failure) {
      if (!active) return;
      if (
        isAxiosError(failure) &&
        failure.response?.status === 404 &&
        cursor.current === 0
      ) {
        // A started owner may not have its first persisted final yet.
        setLoading(false);
      } else {
        setLoading(false);
        setError('load');
      }
    } finally {
      busy = false;
    }
  };

  const subscribe = () => {
    if (!active || terminal) return;
    try {
      const next = new WebSocket(
        liveSubscriptionUrl(sessionId, cursor.current)
      );
      socket = next;
      next.onopen = () => {
        if (!active || socket !== next) return;
        setConnected(true);
        setError(previous => (previous === 'connection' ? null : previous));
        void fetchTail();
      };
      next.onmessage = event => {
        if (!active || socket !== next) return;
        try {
          const message: Record<string, unknown> = JSON.parse(
            event.data as string
          );
          if (message.session_id && message.session_id !== sessionId) return;
          if (typeof message.partial === 'string') setPartial(message.partial);
          if (typeof message.sequence === 'number') {
            desiredCursor = Math.max(desiredCursor, message.sequence);
            setPartial('');
            void fetchTail();
          }
          if (message.state === 'stopped' || message.state === 'failed') {
            terminal = true;
            desiredCursor = Math.max(
              desiredCursor,
              Number(message.last_sequence) || 0
            );
            setOwner(previous =>
              previous
                ? {
                    ...previous,
                    state: message.state as 'stopped' | 'failed',
                    last_sequence: desiredCursor
                  }
                : null
            );
            setPartial('');
            if (message.state === 'failed') setError('connection');
            void fetchTail();
          }
        } catch {
          setError('connection');
        }
      };
      next.onerror = () => {
        if (active && socket === next) setError('connection');
      };
      next.onclose = () => {
        if (!active || socket !== next) return;
        setConnected(false);
        setPartial('');
        if (!terminal) {
          setError('connection');
          reconnect = setTimeout(subscribe, 2000);
        }
      };
    } catch {
      setError('connection');
    }
  };

  const refresh = async () => {
    await fetchTail();
    if (endedFromApi) return;
    try {
      const status = await getLiveSession(sessionId);
      if (!active) return;
      setOwner(status);
      terminal = status.state === 'stopped' || status.state === 'failed';
      desiredCursor = Math.max(desiredCursor, status.last_sequence);
      if (!terminal && (!socket || socket.readyState === WebSocket.CLOSED))
        subscribe();
    } catch {
      if (active && !endedFromApi) setError('connection');
    }
  };

  void fetchTail();
  void getLiveSession(sessionId)
    .then(status => {
      if (!active || (endedFromApi && status.state !== 'failed')) return;
      setOwner(status);
      desiredCursor = status.last_sequence;
      terminal = status.state === 'stopped' || status.state === 'failed';
      if (!terminal) subscribe();
      void fetchTail();
    })
    .catch(() => {
      if (active && !endedFromApi) setError('connection');
      // The replay lease can expire while the durable document remains usable.
    });
  const poll = setInterval(() => {
    if (!terminal || cursor.current < desiredCursor) void fetchTail();
  }, 5000);
  const dispose = () => {
    active = false;
    abort.abort();
    clearInterval(poll);
    clearTimeout(reconnect);
    clearTimeout(retry);
    if (socket) {
      socket.onclose = null;
      socket.close();
    }
    // Viewer teardown never sends Stop or creates another ingestion job.
  };
  return { refresh, dispose };
};
